package com.sddamico.draftmachine.ds.feedback

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.shadowInset
import com.sddamico.draftmachine.ds.transitionRaw
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import androidx.compose.runtime.key
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin

enum class GaugeTone { Brass, Rust, Verde, Glass, Patina }

private fun GaugeTone.color(): Color = when (this) {
  GaugeTone.Brass -> DmColor.brass500
  GaugeTone.Rust -> DmColor.rust500
  GaugeTone.Verde -> DmColor.verde500
  GaugeTone.Glass -> DmColor.glass500
  GaugeTone.Patina -> DmColor.patina500
}

private fun GaugeTone.hex(): String = when (this) {
  GaugeTone.Brass -> "#C07A44"
  GaugeTone.Rust -> "#B4472A"
  GaugeTone.Verde -> "#458C6F"
  GaugeTone.Glass -> "#2FA6A8"
  GaugeTone.Patina -> "#4F7486"
}

/**
 * A brass pressure gauge for draft completion / capacity. Radial dial by default; pass [bar] for a
 * linear pressure readout. [sweepIn] winds the dial up from empty when it appears, the needle
 * swinging past its mark and settling like a real gauge. Port of `components/feedback/Gauge.jsx`.
 */
@Composable
fun Gauge(
  value: Double,
  max: Double = 100.0,
  label: String? = null,
  size: Int = 132,
  bar: Boolean = false,
  tone: GaugeTone = GaugeTone.Brass,
  sweepIn: Boolean = false,
) {
  val pct = (value / max).coerceIn(0.0, 1.0)
  val strokeHex = tone.hex()
  val strokeColor = tone.color()

  if (bar) {
    Div(attrs = Modifier.fillMaxWidth().toAttrs()) {
      if (label != null) {
        Div(
          attrs = Modifier.dmStamp().displayFlex().justifyContent(JustifyContent.SpaceBetween)
            .margin(bottom = 6.px).toAttrs()
        ) {
          SpanText(label)
          SpanText("${(pct * 100).roundToInt()}%", Modifier.color(strokeColor))
        }
      }
      Div(
        attrs = Modifier
          .height(12.px).borderRadius(999.px).backgroundColor(DmColor.surfaceInset).shadowInset()
          .overflow(Overflow.Hidden).border(1.px, LineStyle.Solid, DmColor.borderStrong).toAttrs()
      ) {
        Div(
          attrs = Modifier
            .width((pct * 100).percent).height(100.percent).backgroundColor(strokeColor)
            .boxShadowRaw("0 0 12px $strokeHex")
            .transitionRaw("width ${DmMotion.durSlow} ${DmMotion.easeCog}")
            .toAttrs()
        ) {}
      }
    }
    return
  }

  // Radial dial: 270° sweep. Geometry ported from the JSX, rendered as inline SVG.
  val r = size * 0.38
  val cx = size / 2.0
  val cy = size / 2.0
  val start = 135.0
  val sweep = 270.0
  fun polar(deg: Double): Pair<Double, Double> {
    val a = (deg - 90) * PI / 180
    return Pair(cx + r * cos(a), cy + r * sin(a))
  }
  fun arc(from: Double, to: Double): String {
    val (x1, y1) = polar(from)
    val (x2, y2) = polar(to)
    val large = if (to - from > 180) 1 else 0
    return "M $x1 $y1 A $r $r 0 $large 1 $x2 $y2"
  }
  val needleDeg = start + sweep * pct
  val (nx, ny) = polar(needleDeg)
  val sw = size * 0.07
  // The needle's travel, in whole degrees, names its keyframes so each reading gets its own swing.
  val travel = (sweep * pct).roundToInt()
  val needleName = "dm-gauge-needle-$travel"
  val sweepStyle = if (!sweepIn) "" else """
      <style>
        @keyframes dm-gauge-arc { from { stroke-dashoffset: 100; } }
        @keyframes $needleName { from { transform: rotate(-${travel}deg); } }
      </style>
  """
  val arcMotion = if (!sweepIn) "" else
    "stroke-dasharray:100 100;animation:dm-gauge-arc 900ms ${DmMotion.easeSnap} 120ms both;"
  val needleMotion = if (!sweepIn) "" else
    "transform-box:view-box;transform-origin:${cx}px ${cy}px;animation:$needleName 1100ms ${DmMotion.easeSpring} 120ms both;"
  val svg = """
    <svg width="$size" height="$size" viewBox="0 0 $size $size" xmlns="http://www.w3.org/2000/svg">$sweepStyle
      <path d="${arc(start, start + sweep)}" fill="none" stroke="#161C22" stroke-width="$sw" stroke-linecap="round"/>
      <path d="${arc(start, needleDeg)}" pathLength="100" fill="none" stroke="$strokeHex" stroke-width="$sw" stroke-linecap="round" style="${arcMotion}filter:drop-shadow(0 0 4px $strokeHex)"/>
      <line x1="$cx" y1="$cy" x2="$nx" y2="$ny" stroke="#EAEFF3" stroke-width="2.5" stroke-linecap="round" style="$needleMotion"/>
      <circle cx="$cx" cy="$cy" r="${size * 0.06}" fill="#B0BFCC" stroke="#4B5A68" stroke-width="1.5"/>
      <text x="$cx" y="${cy + size * 0.26}" text-anchor="middle" style="font:700 ${size * 0.2}px 'Space Mono',ui-monospace,monospace;fill:#EAEFF3">${(pct * 100).roundToInt()}</text>
    </svg>
  """.trimIndent()

  Div(
    attrs = Modifier.displayInlineFlex().flexDirection(FlexDirection.Column)
      .alignItems(AlignItems.Center).gap(8.px).toAttrs()
  ) {
    key(svg) {
      Div(attrs = { ref { el -> el.innerHTML = svg; onDispose { } } })
    }
    if (label != null) Div(attrs = Modifier.dmStamp().toAttrs()) { Text(label) }
  }
}
